import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import { pitchesApi } from "../../repositories/pitches";

interface DSPDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedDSPs: string[]) => void;
  initialDSPs: string[];
  mode: 'admin' | 'user';
  pitchId?: number; // Add pitchId for fetching delivery data
}

export default function DSPDeliveryModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialDSPs, 
  mode, 
  pitchId 
}: DSPDeliveryModalProps) {
  const [selectedDSPs, setSelectedDSPs] = useState<string[]>(initialDSPs);
  const [deliveredDSPs, setDeliveredDSPs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const DSPs = ["Spotify", "Apple", "Amazon", "TIDAL", "Deezer", "Audiomack", "SoundCloud","YouTube"];

  useEffect(() => {
    setSelectedDSPs(initialDSPs);
  }, [initialDSPs, isOpen]);

  // Fetch delivery data when modal opens in user mode OR admin editing mode
  useEffect(() => {
    if (isOpen && pitchId) {
      if (mode === 'user') {
        fetchDeliveryData();
      } else if (mode === 'admin' && initialDSPs.length === 0) {
        // If admin is editing but no initial DSPs, fetch from API
        fetchDeliveryData();
      }
    }
  }, [isOpen, mode, pitchId]);

  const fetchDeliveryData = async () => {
    if (!pitchId) return;
    
    setLoading(true);
    try {
      const response = await pitchesApi.getDelivery(pitchId);
      const fetchedDSPs = response.data.data.deliveredDSPs || [];
      
      if (mode === 'user') {
        setDeliveredDSPs(fetchedDSPs);
      } else if (mode === 'admin') {
        // For admin editing, populate the selection with current DSPs
        setSelectedDSPs(fetchedDSPs);
      }
    } catch (error) {
      console.error('Failed to fetch delivery data:', error);
      if (mode === 'user') {
        setDeliveredDSPs(initialDSPs);
      }
    } finally {
      setLoading(false);
    }
  };

  // Rest of the component remains the same...
  const toggleDSP = (dsp: string) => {
    if (mode === 'admin') {
      setSelectedDSPs((prev) =>
        prev.includes(dsp) ? prev.filter((item) => item !== dsp) : [...prev, dsp]
      );
    }
  };

  const handleSubmit = () => {
    if (mode === 'admin') {
      onSubmit(selectedDSPs);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <Dialog.Title className="text-lg font-semibold mb-4">
          {mode === 'admin' ? 'Select DSPs for Delivery' : 'Pitch Delivered To'}
        </Dialog.Title>
        
        <div className="space-y-2">
          {mode === 'admin' ? (
            // Admin mode: checkboxes for selection
            loading ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
            ) : (
              DSPs.map((dsp) => (
                <label key={dsp} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedDSPs.includes(dsp)}
                    onChange={() => toggleDSP(dsp)}
                    className="h-4 w-4"
                  />
                  <span>{dsp}</span>
                </label>
              ))
            )
          ) : (
            // User mode: display delivered DSPs from API
            <div className="space-y-2">
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
              ) : deliveredDSPs.length > 0 ? (
                deliveredDSPs.map((dsp) => (
                  <div key={dsp} className="flex items-center space-x-2 p-2 bg-green-50 rounded-md">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-800 font-medium">{dsp}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No DSPs selected for delivery</p>
              )}
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            {mode === 'admin' ? 'Cancel' : 'Close'}
          </button>
          {mode === 'admin' && !loading && (
            <button
              onClick={handleSubmit}
              disabled={selectedDSPs.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 hover:bg-blue-700"
            >
              Update Delivery
            </button>
          )}
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}